import { useMutation, useQuery } from "@apollo/client"
import { Button, CircularProgress, Grid } from "@material-ui/core"
import React, { useEffect, useState } from "react"
import { useSelector } from "react-redux"
import { RootState } from "../../state/rootReducer"
import {
  ASSIGN_COGNITO_USERNAME,
  RELINK_COGNITO_USERNAME,
  MERGE_USER_MOBILE,
  GET_EMPLOYEE_BY_ID_NUMBER,
} from "./graphql"
import MainContainer from "./layout/MainContainer"
import UserNotFoundView from "./User404"
import { useDispatch } from "react-redux"
import { setPvOption } from "../auth"

const Dashboard: React.FC<{}> = () => {
  const [AssignCognitoUserName] = useMutation(ASSIGN_COGNITO_USERNAME)
  const [RelinkCognitoUserName] = useMutation(RELINK_COGNITO_USERNAME)
  const [UpdateEmployeeMobile] = useMutation(MERGE_USER_MOBILE)
  const dispatch = useDispatch()
  const currentUser = useSelector<RootState>(
    (state) => state.auth.currentUser,
  ) as any
  const [syncing, setSyncing] = useState(false)
  const [syncError, setSyncError] = useState("")
  const syncAttempt = React.useRef("")
  const idNumber = currentUser?.["custom:id_number"]?.trim()
  const subject = currentUser?.sub
  const { data, loading, refetch, error } = useQuery(
    GET_EMPLOYEE_BY_ID_NUMBER,
    {
      variables: {
        employeesID: idNumber,
      },
      skip: !idNumber || !subject,
      fetchPolicy: "network-only",
      onCompleted: (data) => {
        const pvOption = data?.employees?.[0]?.pvOption
        dispatch(setPvOption(pvOption))
      },
    },
  )

  const employee = data?.employees?.length === 1 ? data.employees[0] : null
  const hasMismatch = Boolean(
    employee?.cognitoUsername && employee.cognitoUsername !== subject,
  )

  useEffect(() => {
    if (loading || !employee || !subject || syncing || syncError) return
    const mobile = currentUser.phone_number?.replace(/^\+27/, "0")
    const needsLink = employee.cognitoUsername !== subject
    const needsMobile = mobile && employee.mobile !== mobile
    if (!needsLink && !needsMobile) return
    // At most one attempt per observed state; failed syncs require explicit retry.
    const attempt = JSON.stringify([
      subject,
      employee.id,
      employee.cognitoUsername,
      employee.mobile,
      mobile,
    ])
    if (syncAttempt.current === attempt) return
    syncAttempt.current = attempt
    const synchronize = async (): Promise<void> => {
      setSyncing(true)
      setSyncError("")
      try {
        if (needsLink) {
          const assign = hasMismatch
            ? RelinkCognitoUserName
            : AssignCognitoUserName
          const result = await assign({
            variables: { employee: employee.id },
          })
          const payload = result.data?.assignCognitoUsername
          if (
            payload?.__typename !== "UpdateCurrentUserSuccess" ||
            payload.employee?.id !== employee.id
          ) {
            throw new Error(
              payload?.message ||
                "Unable to link your account. Please contact your administrator.",
            )
          }
        }
        // Verify linking before any follow-up update.
        const linked = needsLink
          ? (await refetch()).data?.employees?.[0]
          : employee
        if (linked?.id !== employee.id || linked?.cognitoUsername !== subject) {
          throw new Error(
            "Your account link could not be verified. Please contact your administrator.",
          )
        }
        if (needsMobile) {
          const result = await UpdateEmployeeMobile({
            variables: { employee: employee.id },
          })
          const payload = result.data?.mergeUserMobileNumber
          if (
            payload?.__typename !== "UpdateCurrentUserSuccess" ||
            payload.employee?.id !== employee.id
          ) {
            throw new Error(
              payload?.message ||
                "Unable to update your mobile number. Please try again.",
            )
          }
          const updated = (await refetch()).data?.employees?.[0]
          if (
            updated?.mobile !== mobile ||
            updated?.cognitoUsername !== subject
          ) {
            throw new Error(
              "Your account update could not be verified. Please try again.",
            )
          }
        }
      } catch (err) {
        setSyncError(err.message)
      } finally {
        setSyncing(false)
      }
    }
    void synchronize()
  }, [
    loading,
    employee,
    subject,
    hasMismatch,
    syncing,
    syncError,
    currentUser.phone_number,
    AssignCognitoUserName,
    RelinkCognitoUserName,
    UpdateEmployeeMobile,
    refetch,
  ])

  const accountError =
    !idNumber || !subject
      ? "Your account is missing identity details. Please contact your administrator."
      : syncError || error?.message

  if (accountError)
    return (
      <Grid container direction="column">
        <p role="alert">{accountError}</p>
        <Button onClick={() => window.location.reload()}>Reload</Button>
      </Grid>
    )

  if (loading || syncing || (employee && employee.cognitoUsername !== subject))
    return (
      <Grid
        container
        style={{
          position: "fixed",
          alignItems: "cennter",
          bottom: "45%",
          left: "45%",
        }}
      >
        <Grid item xs={12}>
          <CircularProgress />
        </Grid>
      </Grid>
    )

  const employeeObj = employee
  if (!employee) {
    return (
      <Grid container>
        <Grid container>
          <UserNotFoundView />
        </Grid>
      </Grid>
    )
  }

  return (
    <Grid container>
      <Grid container>
        <MainContainer employeeObj={employeeObj} refetch={refetch} />
      </Grid>
    </Grid>
  )
}

export default Dashboard
